"use client";

import { useMemo, useCallback } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { X } from "lucide-react";
import type { Category } from "@/types";

interface ActiveProductFiltersProps {
  categories: Category[];
  categorySlug?: string;
}

export function ActiveProductFilters({ categories, categorySlug }: ActiveProductFiltersProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const updateParam = useCallback(
    (key: string, value: string | null) => {
      const params = new URLSearchParams(searchParams.toString());
      if (!value) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
      params.delete("page");
      let targetPath = pathname;
      if (key === "category") {
        params.delete("category");
        targetPath = value ? `/kategori/${value}` : "/products";
      }
      const query = params.toString();
      router.push(query ? `${targetPath}?${query}` : targetPath, { scroll: false });
    },
    [pathname, router, searchParams]
  );

  const activeFilters = useMemo(() => {
    const category = searchParams.get("category") ?? categorySlug ?? "";
    const minPrice = searchParams.get("minPrice") ?? "";
    const maxPrice = searchParams.get("maxPrice") ?? "";
    const onSale = searchParams.get("onSale") === "true";
    const inStockParam = searchParams.get("inStock");
    const inStock = inStockParam === "true";
    const selectedCategory = categories.find((cat) => cat.slug === category);

    return [
      ...(selectedCategory ? [{ key: "category", label: selectedCategory.name, onRemove: () => updateParam("category", null) }] : []),
      ...(minPrice ? [{ key: "minPrice", label: `Min ${minPrice} ₺`, onRemove: () => updateParam("minPrice", null) }] : []),
      ...(maxPrice ? [{ key: "maxPrice", label: `Max ${maxPrice} ₺`, onRemove: () => updateParam("maxPrice", null) }] : []),
      ...(onSale ? [{ key: "onSale", label: "İndirimli", onRemove: () => updateParam("onSale", null) }] : []),
      ...(inStock ? [{ key: "inStock", label: "Stokta", onRemove: () => updateParam("inStock", "false") }] : []),
    ];
  }, [categories, categorySlug, searchParams, updateParam]);

  if (activeFilters.length === 0) return null;

  return (
    <div className="mb-5 flex flex-wrap items-center gap-2">
      {activeFilters.map((filter) => (
        <button
          key={filter.key}
          type="button"
          onClick={filter.onRemove}
          className="inline-flex min-h-8 items-center gap-1.5 rounded-full border border-[var(--sf-accent)]/35 bg-[var(--sf-accent-light)]/55 px-3 text-xs font-semibold text-gray-900 transition-colors hover:border-[var(--sf-accent)] dark:border-white/15 dark:bg-white/[0.06] dark:text-gray-100"
        >
          {filter.label}
          <X className="h-3 w-3" />
        </button>
      ))}
      <button
        type="button"
        onClick={() => router.push(categorySlug ? "/products" : pathname, { scroll: false })}
        className="inline-flex min-h-8 items-center rounded-full border border-gray-200 px-3 text-xs font-semibold text-gray-500 transition-colors hover:border-gray-400 hover:text-gray-950 dark:border-white/10 dark:text-gray-400 dark:hover:text-white"
      >
        Tümünü temizle
      </button>
    </div>
  );
}
